/**
 * "3 days ago" style relative time for callout/comment timestamps; falls back
 * to the raw string on a parse failure. Used by CalloutPanel.
 *
 * Accepts BOTH timestamp shapes the wire has been observed to carry:
 *   - ISO-8601 strings ("2026-08-14T18:05:00Z")
 *   - epoch numbers as string or number ("1787232770.593" — SECONDS with a
 *     fractional part; anything >= 1e12 is treated as milliseconds). Verified
 *     against a real listMetricComments response: Date.parse on the epoch
 *     string is NaN, which rendered the raw number next to the author.
 */
export function relativeTime(timestamp: string | number): string {
  const then = parseTimestamp(timestamp);
  if (then === null) return String(timestamp);
  const seconds = Math.round((Date.now() - then) / 1000);
  const units: Array<[number, string]> = [
    [60, 'second'],
    [60, 'minute'],
    [24, 'hour'],
    [7, 'day'],
    [4.35, 'week'],
    [12, 'month'],
    [Number.POSITIVE_INFINITY, 'year'],
  ];
  let value = seconds;
  let unit = 'second';
  for (const [size, name] of units) {
    if (Math.abs(value) < size) {
      unit = name;
      break;
    }
    value = Math.round(value / size);
    unit = name;
  }
  if (value <= 0) return 'just now';
  return `${value} ${unit}${value === 1 ? '' : 's'} ago`;
}

/** Epoch-seconds/millis (string or number) or ISO string → millis, else null. */
function parseTimestamp(timestamp: string | number): number | null {
  const asNumber =
    typeof timestamp === 'number'
      ? timestamp
      : /^\d+(\.\d+)?$/.test(timestamp.trim())
      ? Number(timestamp)
      : NaN;
  if (Number.isFinite(asNumber) && asNumber > 0) {
    // Epoch seconds until ~33658-09-27; anything >= 1e12 must be millis.
    return asNumber >= 1e12 ? asNumber : asNumber * 1000;
  }
  const parsed = Date.parse(String(timestamp));
  return Number.isNaN(parsed) ? null : parsed;
}
